import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import client from "../lib/api";
import { apiErr } from "../lib/api";
import { Button } from "./ui";
import { tp } from "../i18n";
import { X, Plus, Trash2, FileDown, Printer, Send, RotateCcw, CheckCircle2, Loader2, Upload } from "lucide-react";

// Base WHODIE shift elements — a per-day hour box each.
const BASE_WHODIE = [
  { key: "weekend", label: "Weekend", short: "Wknd" }, { key: "holiday", label: "Holiday", short: "Hol" },
  { key: "overnight", label: "Overnight", short: "Ovn" }, { key: "daytime", label: "Daytime", short: "Day" },
  { key: "integrated", label: "Integrated", short: "Intg" }, { key: "evening", label: "Evening", short: "Eve" },
];
// Overtime tiers — a single OT hours box per day; the multiplier picks which tier it lands in.
const OT_TIERS = [
  { key: "ot_15", label: "Overtime ×1.5", short: "OT×1.5", mult: "1.5" },
  { key: "ot_20", label: "Overtime ×2.0", short: "OT×2.0", mult: "2.0" },
  { key: "ot_25", label: "Overtime ×2.5", short: "OT×2.5", mult: "2.5" },
];
const OT_MULTS = ["1.5", "2.0", "2.5"];
const multToTier = (m) => (m === "2.0" ? "ot_20" : m === "2.5" ? "ot_25" : "ot_15");
// Approved-absence categories + their reason checkmark groups.
const ABS_CATS = [
  { key: "abs_red", label: "Employee (RED)", short: "RED", reasons: [
    { key: "relocation", label: "Relocation" }, { key: "education", label: "Education" }, { key: "development", label: "Development" } ] },
  { key: "abs_pact", label: "Employer (PACT)", short: "PACT", reasons: [
    { key: "project", label: "Project" }, { key: "assignment", label: "Assignment" }, { key: "conference", label: "Conference" }, { key: "travel", label: "Travel" } ] },
  { key: "abs_shampoo", label: "Regulatory (SHAMPOO)", short: "SHAMPOO", reasons: [
    { key: "sickness", label: "Sickness" }, { key: "holiday", label: "Holiday" }, { key: "adoption", label: "Adoption" },
    { key: "maternity", label: "Maternity" }, { key: "paternity", label: "Paternity" }, { key: "official_holiday", label: "Official holiday" }, { key: "other", label: "Other regulatory" } ] },
];
// Every element that gets a rate row in the differentials table (with Wk1/Wk2/Period totals).
const ALL_RATE = [...BASE_WHODIE, ...OT_TIERS, ...ABS_CATS];

const WK_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DEFAULT_RULES = {
  std_start: "09:00", std_end: "17:00", lunch_minutes: "30", lunch_paid: false,
  day_start: "06:00", day_end: "18:00", eve_start: "18:00", eve_end: "22:00",
  weekend_days: ["Sat", "Sun"], weekend_note: "", holidays: [],
};
// Parse a 24-hour HH:MM (or HHMM) string to minutes-since-midnight, or null.
const parseHM = (s) => {
  const m = String(s || "").trim().match(/^(\d{1,2}):?(\d{2})$/);
  if (!m) return null;
  const h = +m[1], mi = +m[2];
  return h > 23 || mi > 59 ? null : h * 60 + mi;
};
// Base worked hours = (end - start, wrapping past midnight) minus unpaid lunch.
const computeBase = (start, end, lunchMin, lunchPaid) => {
  const s = parseHM(start), e = parseHM(end);
  if (s == null || e == null) return null;
  let span = e - s; if (span < 0) span += 24 * 60;
  const net = span - (lunchPaid ? 0 : (Number(lunchMin) || 0));
  return Math.round((net / 60) * 100) / 100;
};
const LUNCH = ["0", "30", "60", "90", "120"];
const STATUS_META = {
  draft: { label: "Draft", cls: "bg-black/10 dark:bg-white/15" },
  sent: { label: "Sent", cls: "bg-[#0F62FE]/20 text-[#0F62FE]" },
  returned: { label: "Returned", cls: "bg-amber-400/25 text-amber-700 dark:text-amber-300" },
  processed: { label: "Processed", cls: "bg-emerald-500/20 text-emerald-700 dark:text-emerald-300" },
};

export default function RateCard({ open, onClose, personId, treeId, header, gradeNum, lang }) {
  const [cards, setCards] = useState([]);
  const [card, setCard] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [absEdit, setAbsEdit] = useState(null);
  const [rules, setRules] = useState(DEFAULT_RULES);
  const [rulesOpen, setRulesOpen] = useState(false);
  const fileRef = useRef(null);
  const locKey = (header && header.location) || "";
  const T = (s) => tp(s, lang);

  const load = useCallback(async () => {
    if (!personId) return;
    const { data } = await client.get(`/rate-cards?person_id=${personId}`);
    setCards(data || []);
    if (data && data.length) setCard(data[0]);
    else setCard(null);
  }, [personId]);

  useEffect(() => { if (open) { setMsg(""); load(); } }, [open, load]);

  // Load this location's HR work rules; on the first entry for a location (none saved), open the setup popup.
  useEffect(() => {
    if (!open) return;
    let alive = true;
    client.get(`/rate-cards/work-rules?location=${encodeURIComponent(locKey)}`)
      .then((r) => {
        if (!alive) return;
        if (r.data) setRules({ ...DEFAULT_RULES, ...r.data });
        else { setRules(DEFAULT_RULES); setRulesOpen(true); }
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [open, locKey]);

  const saveRules = async () => {
    try {
      await client.put("/rate-cards/work-rules", { location: locKey, rules });
      setRulesOpen(false);
      setMsg(T("Location work rules saved."));
    } catch (e) { setMsg(apiErr(e)); }
  };
  const setRule = (k, v) => setRules((p) => ({ ...p, [k]: v }));
  const holidaySet = useMemo(() => new Set(rules.holidays || []), [rules]);

  const createCard = async () => {
    setBusy(true);
    try {
      const today = new Date();
      const monday = new Date(today); monday.setDate(today.getDate() - ((today.getDay() + 6) % 7));
      const end = new Date(monday); end.setDate(monday.getDate() + 13);
      const iso = (d) => d.toISOString().slice(0, 10);
      const { data } = await client.post("/rate-cards", {
        person_id: personId, tree_id: treeId || "",
        period_start: iso(monday), period_end: iso(end),
        header: { ...(header || {}), grade_redacted: false },
      });
      await load(); setCard(data); setMsg(T("New rate card created."));
    } catch (e) { setMsg(apiErr(e)); }
    setBusy(false);
  };

  const patch = (fields) => setCard((c) => ({ ...c, ...fields }));
  const setHeader = (k, v) => setCard((c) => ({ ...c, header: { ...(c.header || {}), [k]: v } }));
  const setDiff = (k, field, v) => setCard((c) => ({ ...c, differentials: { ...(c.differentials || {}), [k]: { ...((c.differentials || {})[k] || {}), [field]: v } } }));
  const setRow = (i, field, v) => setCard((c) => { const ts = [...(c.timesheet || [])]; ts[i] = { ...ts[i], [field]: v }; return { ...c, timesheet: ts }; });
  // Start/End/Lunch changes auto-fill Base from the times (24h), honouring paid/unpaid lunch.
  const setTime = (i, field, v) => setCard((c) => {
    const ts = [...(c.timesheet || [])];
    const row = { ...ts[i], [field]: v };
    const b = computeBase(field === "start" ? v : row.start, field === "end" ? v : row.end, row.lunch, rules.lunch_paid);
    if (b != null) row.hours = String(b);
    ts[i] = row; return { ...c, timesheet: ts };
  });
  const setLunch = (i, v) => setCard((c) => {
    const ts = [...(c.timesheet || [])];
    const row = { ...ts[i], lunch: v };
    const b = computeBase(row.start, row.end, v, rules.lunch_paid);
    if (b != null) row.hours = String(b);
    ts[i] = row; return { ...c, timesheet: ts };
  });
  const setCat = (i, k, v) => setCard((c) => { const ts = [...(c.timesheet || [])]; ts[i] = { ...ts[i], cats: { ...(ts[i].cats || {}), [k]: v } }; return { ...c, timesheet: ts }; });
  const setAbs = (i, field, v) => setCard((c) => { const ts = [...(c.timesheet || [])]; ts[i] = { ...ts[i], absence: { ...(ts[i].absence || { cat: "", reasons: {}, hours: "" }), [field]: v } }; return { ...c, timesheet: ts }; });
  const setAbsReason = (i, rk, v) => setCard((c) => { const ts = [...(c.timesheet || [])]; const a = { ...(ts[i].absence || { cat: "", reasons: {}, hours: "" }) }; a.reasons = { ...(a.reasons || {}), [rk]: v }; ts[i] = { ...ts[i], absence: a }; return { ...c, timesheet: ts }; });
  const setList = (list, i, field, v) => setCard((c) => { const arr = [...(c[list] || [])]; arr[i] = { ...arr[i], [field]: v }; return { ...c, [list]: arr }; });
  const setSignoff = (i, field, v) => setCard((c) => { const arr = [...(c.signoffs || [])]; arr[i] = { ...arr[i], [field]: v }; return { ...c, signoffs: arr }; });

  const pullGradeDiffs = async () => {
    if (!gradeNum) { setMsg(T("Set the person's salary grade first.")); return; }
    try {
      const { data } = await client.get("/grade-differentials");
      const g = (data.grades || {})[String(gradeNum)];
      if (!g) { setMsg(T("No saved differentials for this grade yet.")); return; }
      setCard((c) => ({ ...c, differentials: g })); setMsg(`${T("Pulled differentials from Grade")} ${gradeNum}`);
    } catch (e) { setMsg(apiErr(e)); }
  };
  const saveGradeDiffs = async () => {
    if (!gradeNum || !card) { setMsg(T("Set the person's salary grade first.")); return; }
    try { await client.put("/grade-differentials", { grade: String(gradeNum), differentials: card.differentials }); setMsg(`${T("Saved as Grade")} ${gradeNum} ${T("defaults")}`); }
    catch (e) { setMsg(apiErr(e)); }
  };

  const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
  const totals = useMemo(() => {
    const wk1 = {}, wk2 = {}, period = {};
    ALL_RATE.forEach((e) => { wk1[e.key] = 0; wk2[e.key] = 0; period[e.key] = 0; });
    const reasonP = {}, reasonW1 = {}, reasonW2 = {};
    let baseSum = 0, grandDay = 0;
    const rows = [];
    (card?.timesheet || []).forEach((r, i) => {
      const wk = i < 7 ? wk1 : wk2;
      const base = Number(r.hours) || 0;
      baseSum += base;
      let dt = 0;
      BASE_WHODIE.forEach((e) => {
        const ch = Number((r.cats || {})[e.key]) || 0;
        dt += ch; period[e.key] += ch; wk[e.key] += ch;
      });
      const ot = Number((r.cats || {}).overtime) || 0;
      if (ot) { const tk = multToTier(r.ot_mult || "1.5"); dt += ot; period[tk] += ot; wk[tk] += ot; }
      const ab = r.absence || {}; const ah = Number(ab.hours) || 0;
      if (ah && ab.cat && period[ab.cat] !== undefined) {
        dt += ah; period[ab.cat] += ah; wk[ab.cat] += ah;
        Object.keys(ab.reasons || {}).forEach((rk) => {
          if (ab.reasons[rk]) {
            reasonP[rk] = (reasonP[rk] || 0) + ah;
            if (i < 7) reasonW1[rk] = (reasonW1[rk] || 0) + ah; else reasonW2[rk] = (reasonW2[rk] || 0) + ah;
          }
        });
      }
      dt = round2(dt); grandDay += dt;
      rows[i] = { dayTotal: dt, base: round2(base), delta: round2(dt - base) };
    });
    return { wk1, wk2, period, reasonP, reasonW1, reasonW2, baseSum: round2(baseSum), grandDay: round2(grandDay), rows };
  }, [card]);

  const save = async () => {
    if (!card) return;
    setBusy(true);
    try {
      await client.put(`/rate-cards/${card.id}`, {
        period_start: card.period_start, period_end: card.period_end, period_label: card.period_label,
        header: card.header, differentials: card.differentials,
        additions: card.additions, deductions: card.deductions, timesheet: card.timesheet, signoffs: card.signoffs,
      });
      setMsg(T("Saved ✓")); await load();
    } catch (e) { setMsg(apiErr(e)); }
    setBusy(false);
  };

  const setStatus = async (status) => {
    if (!card) return;
    setBusy(true);
    try { await save(); const { data } = await client.post(`/rate-cards/${card.id}/status`, { status }); setCard(data); await load(); setMsg(T("Status updated")); }
    catch (e) { setMsg(apiErr(e)); }
    setBusy(false);
  };

  const removeCard = async () => {
    if (!card || !window.confirm(T("Delete this rate card?"))) return;
    await client.delete(`/rate-cards/${card.id}`); await load();
  };

  const downloadPdf = async () => {
    if (!card) return;
    setBusy(true);
    try {
      await save();
      const res = await client.get(`/rate-cards/${card.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = "rate_card.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setMsg(apiErr(e)); }
    setBusy(false);
  };

  const importPdf = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f || !card) { return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await client.post(`/rate-cards/${card.id}/import-pdf`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCard(data); await load(); setMsg(T("Returned PDF imported — timesheet updated."));
    } catch (err) { setMsg(apiErr(err)); }
    setBusy(false);
    e.target.value = "";
  };

  if (!open) return null;
  const h = card?.header || {};

  const moneyToggle = (val, onAmt, onMode, cash) => (
    <div className="flex items-center gap-1">
      <span className="w-3 text-center text-[10px] opacity-60">{val?.mode === "percent" ? "%" : (cash ? "$" : "$")}</span>
      <input value={val?.amount || ""} onChange={(e) => onAmt(e.target.value)} inputMode="decimal"
        className="w-16 rounded border border-black/15 bg-transparent px-1 py-0.5 text-[11px] dark:border-white/15" />
      <div className="flex overflow-hidden rounded border border-black/15 text-[9px] dark:border-white/15">
        <button type="button" onClick={() => onMode("figure")} className={`px-1 py-0.5 ${val?.mode !== "percent" ? "bg-[var(--primary)] text-white" : "opacity-70"}`}>{cash ? T("Cash") : T("Fig")}</button>
        <button type="button" onClick={() => onMode("percent")} className={`px-1 py-0.5 ${val?.mode === "percent" ? "bg-[var(--primary)] text-white" : "opacity-70"}`}>%</button>
      </div>
    </div>
  );

  return createPortal(
    <div className="fixed inset-0 z-[120] flex flex-col bg-black/50 backdrop-blur-sm" data-testid="rate-card-overlay">
      <div className="m-2 flex flex-1 flex-col overflow-hidden rounded-xl bg-white shadow-2xl dark:bg-neutral-900">
        {/* toolbar */}
        <div className="flex flex-wrap items-center gap-2 border-b border-black/10 px-4 py-2 dark:border-white/10">
          <span className="font-head text-lg">{T("Timesheet")}</span>
          {card && <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS_META[card.status]?.cls || ""}`} data-testid="rate-card-status">{T(STATUS_META[card.status]?.label || card.status)}</span>}
          <div className="ms-auto flex flex-wrap items-center gap-2">
            {card && <>
              <Button variant="ghost" onClick={save} disabled={busy} data-testid="rate-card-save">{busy ? <Loader2 size={15} className="animate-spin" /> : null} {T("Save")}</Button>
              <Button variant="ghost" onClick={() => setStatus("sent")} disabled={busy} data-testid="rate-card-send"><Send size={14} /> {T("Send")}</Button>
              <Button variant="ghost" onClick={() => setStatus("returned")} disabled={busy} data-testid="rate-card-returned"><RotateCcw size={14} /> {T("Mark returned")}</Button>
              <Button variant="ghost" onClick={() => setStatus("processed")} disabled={busy} data-testid="rate-card-processed"><CheckCircle2 size={14} /> {T("Process to payroll")}</Button>
              <Button variant="ghost" onClick={downloadPdf} disabled={busy} data-testid="rate-card-pdf"><FileDown size={14} /> {T("Fillable PDF")}</Button>
              <Button variant="ghost" onClick={() => fileRef.current && fileRef.current.click()} disabled={busy} data-testid="rate-card-import"><Upload size={14} /> {T("Import returned PDF")}</Button>
              <input ref={fileRef} type="file" accept="application/pdf" onChange={importPdf} className="hidden" data-testid="rate-card-import-input" />
              <Button variant="ghost" onClick={() => window.print()} data-testid="rate-card-print"><Printer size={14} /> {T("Print")}</Button>
            </>}
            <button onClick={onClose} data-testid="rate-card-close" className="rounded p-1 opacity-70 hover:opacity-100"><X size={20} /></button>
          </div>
        </div>
        {msg && <div className="bg-[var(--primary)]/8 px-4 py-1 text-xs" data-testid="rate-card-msg">{msg}</div>}

        <div className="flex flex-1 overflow-hidden">
          {/* periods sidebar */}
          <div className="w-44 shrink-0 overflow-y-auto border-e border-black/10 p-2 dark:border-white/10">
            <Button className="mb-2 w-full" onClick={createCard} disabled={busy} data-testid="rate-card-new"><Plus size={14} /> {T("New period")}</Button>
            {cards.map((c) => (
              <button key={c.id} onClick={() => setCard(c)} data-testid={`rate-card-item-${c.id}`}
                className={`mb-1 block w-full rounded px-2 py-1 text-start text-[11px] lift ${card?.id === c.id ? "bg-[var(--primary)]/12" : "hover:bg-black/5 dark:hover:bg-white/10"}`}>
                <span className="block font-medium">{c.period_start || T("(no period)")}</span>
                <span className={`inline-block rounded-full px-1.5 text-[9px] ${STATUS_META[c.status]?.cls || ""}`}>{T(STATUS_META[c.status]?.label || c.status)}</span>
              </button>
            ))}
            {!cards.length && <p className="text-[11px] opacity-60">{T("No rate cards yet. Create one for a pay period.")}</p>}
          </div>

          {/* card body */}
          <div className="flex-1 overflow-auto p-4" data-testid="rate-card-body">
            {!card ? (
              <div className="grid h-full place-items-center text-sm opacity-60">{T("Select or create a rate card period.")}</div>
            ) : (
              <div className="space-y-4">
                {/* header (auto-filled, editable) */}
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <Field label={T("Location")} v={h.location} on={(v) => setHeader("location", v)} tid="rc-location" />
                  <Field label={T("First name")} v={h.first} on={(v) => setHeader("first", v)} tid="rc-first" />
                  <Field label={T("Last name")} v={h.last} on={(v) => setHeader("last", v)} tid="rc-last" />
                  <div>
                    <label className="mb-0.5 block text-[11px] font-medium opacity-70">{T("Employee grade")}</label>
                    <div className="flex items-center gap-1">
                      <input value={h.grade || ""} onChange={(e) => setHeader("grade", e.target.value)} data-testid="rc-grade"
                        className={`w-full rounded border border-black/15 bg-transparent px-2 py-1 text-sm dark:border-white/15 ${h.grade_redacted ? "line-through opacity-40" : ""}`} />
                      <label className="flex items-center gap-1 whitespace-nowrap text-[10px]" title={T("Hash out the grade when releasing the card")}>
                        <input type="checkbox" checked={!!h.grade_redacted} onChange={(e) => setHeader("grade_redacted", e.target.checked)} data-testid="rc-grade-redact" className="accent-[var(--primary)]" /> {T("redact")}
                      </label>
                    </div>
                  </div>
                  <Field label={T("Principal group")} v={h.principal_group} on={(v) => setHeader("principal_group", v)} tid="rc-principal" />
                  <Field label={T("Secondary group")} v={h.secondary_group} on={(v) => setHeader("secondary_group", v)} tid="rc-secondary" />
                  <Field label={T("Period start")} v={card.period_start} on={(v) => patch({ period_start: v })} type="date" tid="rc-period-start" />
                  <Field label={T("Period end")} v={card.period_end} on={(v) => patch({ period_end: v })} type="date" tid="rc-period-end" />
                </div>

                {/* differentials + additions/deductions */}
                <div className="grid gap-4 lg:grid-cols-[1fr_auto_1fr]">
                  <div className="rounded-md border border-black/10 p-2 dark:border-white/10">
                    <div className="mb-1 flex flex-wrap items-center gap-2">
                      <span className="text-xs font-semibold opacity-70">{T("Shift differentials (WHODIE+O)")}</span>
                      <button type="button" onClick={pullGradeDiffs} data-testid="rc-pull-grade" className="ms-auto rounded border border-black/15 px-1.5 py-0.5 text-[10px] font-medium text-[var(--primary)] lift dark:border-white/15">{T("Pull from grade")}</button>
                      <button type="button" onClick={saveGradeDiffs} data-testid="rc-save-grade" className="rounded border border-black/15 px-1.5 py-0.5 text-[10px] font-medium lift dark:border-white/15">{T("Save as grade default")}</button>
                    </div>
                    <table className="w-full text-[11px]" data-testid="rc-diff-table">
                      <thead><tr className="text-start opacity-60">
                        <th className="p-1">{T("Element")}</th>
                        <th className="p-1">{T("Rate")}</th>
                        <th className="p-1 text-center">{T("Wk 1")}</th>
                        <th className="p-1 text-center">{T("Wk 2")}</th>
                        <th className="p-1 text-center">{T("Period")}</th>
                        <th className="p-1 text-center">{T("Est. pay")}</th>
                      </tr></thead>
                      <tbody>
                        {ALL_RATE.map((w) => {
                          const d = (card.differentials || {})[w.key] || {};
                          const amt = Number(d.amount) || 0; const hrs = totals.period[w.key] || 0;
                          const est = !amt || !hrs ? "—" : (d.mode === "percent" ? `${round2(amt)}% ×${round2(hrs)}h` : `$${round2(amt * hrs)}`);
                          return (
                          <tr key={w.key} className="border-t border-black/5 dark:border-white/5" data-testid={`rc-diff-${w.key}`}>
                            <td className="p-1 whitespace-nowrap">{T(w.label)}</td>
                            <td className="p-1">{moneyToggle(d, (v) => setDiff(w.key, "amount", v), (m) => setDiff(w.key, "mode", m), false)}</td>
                            <td className="p-1 text-center" data-testid={`rc-diff-wk1-${w.key}`}>{round2(totals.wk1[w.key])}</td>
                            <td className="p-1 text-center" data-testid={`rc-diff-wk2-${w.key}`}>{round2(totals.wk2[w.key])}</td>
                            <td className="p-1 text-center font-medium" data-testid={`rc-diff-period-${w.key}`}>{round2(totals.period[w.key])}</td>
                            <td className="p-1 text-center whitespace-nowrap text-[var(--primary)]" data-testid={`rc-diff-est-${w.key}`}>{est}</td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="hidden w-px bg-black/10 dark:bg-white/10 lg:block" />
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="mb-1 text-xs font-semibold text-emerald-600">{T("Additions")}</div>
                      {(card.additions || []).slice(0, 7).map((a, i) => (
                        <div key={i} className="mb-1" data-testid={`rc-addition-${i}`}>
                          <input value={a.name || ""} placeholder={T("name")} onChange={(e) => setList("additions", i, "name", e.target.value)}
                            className="mb-0.5 w-full rounded border border-black/15 bg-transparent px-1 py-0.5 text-[11px] dark:border-white/15" />
                          {moneyToggle(a, (v) => setList("additions", i, "amount", v), (m) => setList("additions", i, "mode", m), true)}
                        </div>
                      ))}
                    </div>
                    <div>
                      <div className="mb-1 text-xs font-semibold text-[#DA1E28]">{T("Deductions")}</div>
                      {(card.deductions || []).slice(0, 7).map((d, i) => (
                        <div key={i} className="mb-1" data-testid={`rc-deduction-${i}`}>
                          <input value={d.name || ""} placeholder={T("name")} onChange={(e) => setList("deductions", i, "name", e.target.value)}
                            className="mb-0.5 w-full rounded border border-black/15 bg-transparent px-1 py-0.5 text-[11px] dark:border-white/15" />
                          {moneyToggle(d, (v) => setList("deductions", i, "amount", v), (m) => setList("deductions", i, "mode", m), true)}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* approved-absence reason totals */}
                <div className="rounded-md border border-black/10 p-2 dark:border-white/10" data-testid="rc-reason-totals">
                  <div className="mb-1 text-xs font-semibold opacity-70">{T("Approved absence — reason totals (hours)")}</div>
                  <div className="grid gap-3 sm:grid-cols-3">
                    {ABS_CATS.map((c) => {
                      const sub = c.reasons.reduce((s, rn) => s + (totals.reasonP[rn.key] || 0), 0);
                      return (
                        <div key={c.key} data-testid={`rc-reason-group-${c.key}`}>
                          <div className="mb-1 text-[11px] font-semibold text-[var(--primary)]">{T(c.label)}</div>
                          <table className="w-full text-[11px]">
                            <thead><tr className="text-start opacity-60">
                              <th className="p-0.5">{T("Reason")}</th><th className="p-0.5 text-center">{T("Wk 1")}</th>
                              <th className="p-0.5 text-center">{T("Wk 2")}</th><th className="p-0.5 text-center">{T("Period")}</th>
                            </tr></thead>
                            <tbody>
                              {c.reasons.map((rn) => (
                                <tr key={rn.key} className="border-t border-black/5 dark:border-white/5" data-testid={`rc-reason-${rn.key}`}>
                                  <td className="p-0.5">{T(rn.label)}</td>
                                  <td className="p-0.5 text-center">{round2(totals.reasonW1[rn.key] || 0)}</td>
                                  <td className="p-0.5 text-center">{round2(totals.reasonW2[rn.key] || 0)}</td>
                                  <td className="p-0.5 text-center font-medium" data-testid={`rc-reason-period-${rn.key}`}>{round2(totals.reasonP[rn.key] || 0)}</td>
                                </tr>
                              ))}
                              <tr className="border-t border-black/20 font-semibold dark:border-white/20">
                                <td className="p-0.5" colSpan={3}>{T("Subtotal")}</td>
                                <td className="p-0.5 text-center" data-testid={`rc-reason-subtotal-${c.key}`}>{round2(sub)}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* timesheet */}
                <div className="rounded-md border border-black/10 p-2 dark:border-white/10">
                  <div className="mb-1 flex flex-wrap items-center gap-2">
                    <span className="text-xs font-semibold opacity-70">{T("Timesheet — 2 weeks (Mon → Sun) · supervisor completes & returns")}</span>
                    <button type="button" onClick={() => setRulesOpen(true)} data-testid="rc-open-rules"
                      className="ms-auto rounded border border-black/15 px-2 py-0.5 text-[10px] font-medium lift dark:border-white/15">
                      ⚙ {T("Location rules")}{locKey ? ` · ${locKey}` : ""}
                    </button>
                    <span className="text-[10px] opacity-60">{T("Times are 24-hour; Base auto-fills from Start/End −")} {rules.lunch_paid ? T("paid lunch") : T("unpaid lunch")}</span>
                  </div>
                  <div className="flex gap-3">
                    <div className="w-52 shrink-0" data-testid="rc-signoffs">
                      <div className="mb-1 text-[11px] font-semibold opacity-70">{T("Sign-offs (name + date)")}</div>
                      {(card.signoffs || []).map((s, i) => (
                        <div key={s.role} className="mb-1" data-testid={`rc-signoff-${s.role}`}>
                          <div className="text-[10px] font-medium opacity-70">{T(s.label || s.role)}</div>
                          <div className="flex gap-1">
                            <input value={s.name || ""} placeholder={T("name")} onChange={(e) => setSignoff(i, "name", e.target.value)} data-testid={`rc-signoff-name-${s.role}`} className="min-w-0 flex-1 rounded border border-black/15 bg-transparent px-1 py-0.5 text-[11px] dark:border-white/15" />
                            <input type="date" value={s.date || ""} onChange={(e) => setSignoff(i, "date", e.target.value)} data-testid={`rc-signoff-date-${s.role}`} className="w-28 rounded border border-black/15 bg-transparent px-1 py-0.5 text-[11px] dark:border-white/15" />
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="min-w-0 flex-1 overflow-x-auto">
                    <table className="w-full text-[11px]" data-testid="rc-timesheet">
                      <thead><tr className="text-start opacity-60">
                        <th className="p-1">{T("Day")}</th><th className="p-1">{T("Start")}</th><th className="p-1">{T("End")}</th>
                        <th className="p-1">{T("Lunch")}</th><th className="p-1">{T("Base")}</th>
                        {BASE_WHODIE.map((w) => <th key={w.key} className="p-1 text-center" title={T(w.label)}>{w.short}</th>)}
                        <th className="p-1 text-center" title={T("Overtime")}>{T("OT")}</th>
                        <th className="p-1 text-center" title={T("Approved absence")}>{T("Absence")}</th>
                        <th className="p-1 text-center">{T("Day total")}</th>
                      </tr></thead>
                      <tbody>
                        {(card.timesheet || []).map((r, i) => (
                          <tr key={i} className="border-t border-black/5 dark:border-white/5" data-testid={`rc-ts-row-${i}`}>
                            <td className="whitespace-nowrap p-1 font-medium">
                              {(r.day || "").slice(0, 3)} {r.date}
                              {holidaySet.has(r.date) && <span className="ms-1 rounded bg-amber-500/20 px-1 text-[9px] font-semibold text-amber-700 dark:text-amber-300" data-testid={`rc-ph-${i}`} title={T("Public holiday")}>PH</span>}
                            </td>
                            <td className="p-1"><input value={r.start || ""} placeholder="HH:MM" onChange={(e) => setTime(i, "start", e.target.value)} data-testid={`rc-start-${i}`} title={T("24-hour, e.g. 09:00")} className="w-16 rounded border border-black/15 bg-transparent px-1 py-0.5 dark:border-white/15" /></td>
                            <td className="p-1"><input value={r.end || ""} placeholder="HH:MM" onChange={(e) => setTime(i, "end", e.target.value)} data-testid={`rc-end-${i}`} title={T("24-hour, e.g. 17:00")} className="w-16 rounded border border-black/15 bg-transparent px-1 py-0.5 dark:border-white/15" /></td>
                            <td className="p-1">
                              <select value={r.lunch || "0"} onChange={(e) => setLunch(i, e.target.value)} data-testid={`rc-lunch-${i}`} className="rounded border border-black/15 bg-transparent px-1 py-0.5 dark:border-white/15">
                                {LUNCH.map((l) => <option key={l} value={l}>{l}</option>)}
                              </select>
                            </td>
                            <td className="p-1"><input value={r.hours || ""} inputMode="decimal" onChange={(e) => setRow(i, "hours", e.target.value)} data-testid={`rc-hours-${i}`} className="w-14 rounded border border-black/15 bg-transparent px-1 py-0.5 dark:border-white/15" /></td>
                            {BASE_WHODIE.map((w) => (
                              <td key={w.key} className="p-1">
                                <input value={(r.cats && r.cats[w.key]) || ""} inputMode="decimal" onChange={(e) => setCat(i, w.key, e.target.value)} data-testid={`rc-cat-${i}-${w.key}`} className="w-12 rounded border border-black/15 bg-transparent px-1 py-0.5 text-center dark:border-white/15" />
                              </td>
                            ))}
                            <td className="p-1">
                              <div className="flex items-center gap-0.5">
                                <input value={(r.cats && r.cats.overtime) || ""} inputMode="decimal" onChange={(e) => setCat(i, "overtime", e.target.value)} data-testid={`rc-cat-${i}-overtime`} className="w-10 rounded border border-black/15 bg-transparent px-1 py-0.5 text-center dark:border-white/15" />
                                <select value={r.ot_mult || "1.5"} onChange={(e) => setRow(i, "ot_mult", e.target.value)} data-testid={`rc-otmult-${i}`} className="rounded border border-black/15 bg-transparent px-0.5 py-0.5 text-[10px] dark:border-white/15">
                                  {OT_MULTS.map((m) => <option key={m} value={m}>×{m}</option>)}
                                </select>
                              </div>
                            </td>
                            <td className="p-1 text-center">
                              {(() => {
                                const ab = r.absence || {}; const cc = ABS_CATS.find((c) => c.key === ab.cat);
                                const has = cc && (Number(ab.hours) > 0 || Object.values(ab.reasons || {}).some(Boolean));
                                return (
                                  <button type="button" onClick={() => setAbsEdit(i)} data-testid={`rc-abs-open-${i}`}
                                    className={`rounded border px-1.5 py-0.5 text-[10px] lift ${has ? "border-[var(--primary)] bg-[var(--primary)]/10 text-[var(--primary)] font-medium" : "border-black/15 opacity-70 dark:border-white/15"}`}>
                                    {has ? `${cc.short} ${ab.hours || ""}` : T("Add")}
                                  </button>
                                );
                              })()}
                            </td>
                            <td className="whitespace-nowrap p-1 text-center" data-testid={`rc-day-total-${i}`}>
                              {(() => {
                                const row = totals.rows[i]; if (!row) return null;
                                const off = Math.abs(row.delta) > 0.001;
                                return (
                                  <span className={off ? "font-semibold text-[#DA1E28]" : "font-medium"}>
                                    {row.dayTotal}
                                    {off && <span className="ms-1 text-[9px]" data-testid={`rc-day-flag-${i}`}>{row.delta > 0 ? T("over") : T("short")} {Math.abs(row.delta)}</span>}
                                  </span>
                                );
                              })()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 border-black/20 font-semibold dark:border-white/20" data-testid="rc-totals">
                          <td className="p-1 text-end" colSpan={4}>{T("Totals")}</td>
                          <td className="p-1" data-testid="rc-total-hours">{totals.baseSum}</td>
                          {BASE_WHODIE.map((w) => <td key={w.key} className="p-1 text-center" data-testid={`rc-total-${w.key}`}>{round2(totals.period[w.key])}</td>)}
                          <td className="p-1 text-center" data-testid="rc-total-overtime">{round2(totals.period.ot_15 + totals.period.ot_20 + totals.period.ot_25)}</td>
                          <td className="p-1 text-center" data-testid="rc-total-absence">{round2(totals.period.abs_red + totals.period.abs_pact + totals.period.abs_shampoo)}</td>
                          <td className="p-1 text-center" data-testid="rc-grand-day-total">{totals.grandDay}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  </div>

                  <div className="mt-1 flex justify-end">
                    <Button variant="ghost" onClick={removeCard} className="text-[#DA1E28]" data-testid="rate-card-delete"><Trash2 size={13} /> {T("Delete card")}</Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {absEdit !== null && card && card.timesheet && card.timesheet[absEdit] && (() => {
        const r = card.timesheet[absEdit];
        const ab = r.absence || { cat: "", reasons: {}, hours: "" };
        const cc = ABS_CATS.find((c) => c.key === ab.cat);
        return (
          <div className="fixed inset-0 z-[130] grid place-items-center bg-black/50 p-4" onClick={() => setAbsEdit(null)} data-testid="rc-absence-overlay">
            <div className="w-[440px] max-w-full rounded-xl bg-white p-4 shadow-2xl dark:bg-neutral-900" onClick={(e) => e.stopPropagation()} data-testid="rc-absence-editor">
              <div className="mb-3 flex items-center justify-between">
                <span className="font-head text-sm">{T("Approved absence")} — {(r.day || "").slice(0, 3)} {r.date}</span>
                <button onClick={() => setAbsEdit(null)} data-testid="rc-absence-close" className="rounded p-1 opacity-70 hover:opacity-100"><X size={18} /></button>
              </div>
              <div className="mb-2 flex flex-wrap gap-1">
                {ABS_CATS.map((c) => (
                  <button key={c.key} type="button" onClick={() => setAbs(absEdit, "cat", ab.cat === c.key ? "" : c.key)} data-testid={`rc-abs-cat-${c.key}`}
                    className={`rounded px-2 py-1 text-[11px] font-medium lift ${ab.cat === c.key ? "bg-[var(--primary)] text-white" : "border border-black/15 dark:border-white/15"}`}>{T(c.label)}</button>
                ))}
              </div>
              {cc ? (
                <>
                  <div className="mb-1 text-[10px] font-semibold opacity-60">{T("Reason(s) — the hours below apply to the ticked reason(s)")}</div>
                  <div className="mb-3 grid grid-cols-2 gap-1">
                    {cc.reasons.map((rn) => (
                      <label key={rn.key} className="flex items-center gap-1.5 text-[11px]" data-testid={`rc-abs-reason-${rn.key}`}>
                        <input type="checkbox" checked={!!(ab.reasons || {})[rn.key]} onChange={(e) => setAbsReason(absEdit, rn.key, e.target.checked)} className="accent-[var(--primary)]" />
                        {T(rn.label)}
                      </label>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-medium">{T("Hours")}</span>
                    <input value={ab.hours || ""} inputMode="decimal" onChange={(e) => setAbs(absEdit, "hours", e.target.value)} data-testid="rc-abs-hours" className="w-24 rounded border border-black/15 bg-transparent px-2 py-1 text-sm dark:border-white/15" />
                    <button type="button" onClick={() => setAbsEdit(null)} data-testid="rc-abs-done" className="ms-auto rounded bg-[var(--primary)] px-3 py-1 text-[11px] font-medium text-white lift">{T("Done")}</button>
                  </div>
                </>
              ) : <p className="text-[11px] opacity-60">{T("Pick a category to log an approved absence.")}</p>}
            </div>
          </div>
        );
      })()}
      {rulesOpen && (
        <div className="fixed inset-0 z-[130] grid place-items-center bg-black/50 p-4" onClick={() => setRulesOpen(false)} data-testid="rc-rules-overlay">
          <div className="max-h-[90vh] w-[560px] max-w-full overflow-y-auto rounded-xl bg-white p-4 shadow-2xl dark:bg-neutral-900" onClick={(e) => e.stopPropagation()} data-testid="rc-rules-editor">
            <div className="mb-1 flex items-center justify-between">
              <span className="font-head text-sm">{T("Location work rules")}{locKey ? ` — ${locKey}` : ` — ${T("(default)")}`}</span>
              <button onClick={() => setRulesOpen(false)} data-testid="rc-rules-close" className="rounded p-1 opacity-70 hover:opacity-100"><X size={18} /></button>
            </div>
            <p className="mb-3 text-[11px] opacity-60">{T("Define this location's standard hours, lunch, shift ranges and public holidays. Saved once per location and reused for every timesheet there.")}</p>

            <div className="mb-3 rounded-md border border-black/10 p-2 dark:border-white/10">
              <div className="mb-1 text-[11px] font-semibold text-[var(--primary)]">{T("Standard work day & lunch")}</div>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <Field label={T("Day start")} v={rules.std_start} on={(v) => setRule("std_start", v)} tid="rules-std-start" />
                <Field label={T("Day end")} v={rules.std_end} on={(v) => setRule("std_end", v)} tid="rules-std-end" />
                <Field label={T("Lunch (min)")} v={rules.lunch_minutes} on={(v) => setRule("lunch_minutes", v)} tid="rules-lunch-min" />
                <label className="flex items-end gap-1.5 pb-1 text-[11px]">
                  <input type="checkbox" checked={!!rules.lunch_paid} onChange={(e) => setRule("lunch_paid", e.target.checked)} data-testid="rules-lunch-paid" className="accent-[var(--primary)]" />
                  {T("Lunch is paid")}
                </label>
              </div>
            </div>

            <div className="mb-3 rounded-md border border-black/10 p-2 dark:border-white/10">
              <div className="mb-1 text-[11px] font-semibold text-[var(--primary)]">{T("Shift ranges (24-hour)")}</div>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <Field label={T("Day shift start")} v={rules.day_start} on={(v) => setRule("day_start", v)} tid="rules-day-start" />
                <Field label={T("Day shift end")} v={rules.day_end} on={(v) => setRule("day_end", v)} tid="rules-day-end" />
                <Field label={T("Evening start")} v={rules.eve_start} on={(v) => setRule("eve_start", v)} tid="rules-eve-start" />
                <Field label={T("Evening end")} v={rules.eve_end} on={(v) => setRule("eve_end", v)} tid="rules-eve-end" />
              </div>
              <div className="mt-2 text-[10px] font-medium opacity-70">{T("Weekend shift days")}</div>
              <div className="flex flex-wrap gap-1">
                {WK_DAYS.map((d) => {
                  const on = (rules.weekend_days || []).includes(d);
                  return (
                    <button key={d} type="button" data-testid={`rules-wkday-${d}`}
                      onClick={() => setRule("weekend_days", on ? rules.weekend_days.filter((x) => x !== d) : [...(rules.weekend_days || []), d])}
                      className={`rounded px-2 py-0.5 text-[11px] font-medium lift ${on ? "bg-[var(--primary)] text-white" : "border border-black/15 dark:border-white/15"}`}>{T(d)}</button>
                  );
                })}
              </div>
            </div>

            <div className="mb-3 rounded-md border border-black/10 p-2 dark:border-white/10">
              <div className="mb-1 text-[11px] font-semibold text-[var(--primary)]">{T("Public holidays")}</div>
              <div className="mb-2 flex items-center gap-2">
                <input type="date" data-testid="rules-holiday-add" className="rounded border border-black/15 bg-transparent px-2 py-1 text-sm dark:border-white/15"
                  onChange={(e) => { const v = e.target.value; if (v && !(rules.holidays || []).includes(v)) setRule("holidays", [...(rules.holidays || []), v].sort()); e.target.value = ""; }} />
                <span className="text-[10px] opacity-60">{T("Pick a date to add it")}</span>
              </div>
              <div className="flex flex-wrap gap-1">
                {(rules.holidays || []).map((h) => (
                  <span key={h} className="flex items-center gap-1 rounded bg-amber-500/15 px-2 py-0.5 text-[11px] text-amber-700 dark:text-amber-300" data-testid={`rules-holiday-${h}`}>
                    {h}
                    <button type="button" onClick={() => setRule("holidays", rules.holidays.filter((x) => x !== h))} className="opacity-70 hover:opacity-100"><X size={12} /></button>
                  </span>
                ))}
                {!(rules.holidays || []).length && <span className="text-[11px] opacity-50">{T("None yet")}</span>}
              </div>
            </div>

            <div className="flex items-center justify-end gap-2">
              <button type="button" onClick={() => setRulesOpen(false)} className="rounded border border-black/15 px-3 py-1 text-[11px] font-medium lift dark:border-white/15">{T("Cancel")}</button>
              <button type="button" onClick={saveRules} data-testid="rc-rules-save" className="rounded bg-[var(--primary)] px-3 py-1 text-[11px] font-medium text-white lift">{T("Save rules")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  , document.body);
}

function Field({ label, v, on, type = "text", tid }) {
  return (
    <div>
      <label className="mb-0.5 block text-[11px] font-medium opacity-70">{label}</label>
      <input type={type} value={v || ""} onChange={(e) => on(e.target.value)} data-testid={tid}
        className="w-full rounded border border-black/15 bg-transparent px-2 py-1 text-sm dark:border-white/15" />
    </div>
  );
}
